import * as React from "react"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { Check, ChevronRight, Circle } from "lucide-react"

import { cn } from "@/lib/utils"

const DropdownMenuBase = DropdownMenuPrimitive.Root

const DropdownMenuTriggerBase = DropdownMenuPrimitive.Trigger

const DropdownMenuGroupBase = DropdownMenuPrimitive.Group

const DropdownMenuPortalBase = DropdownMenuPrimitive.Portal

const DropdownMenuSubBase = DropdownMenuPrimitive.Sub

const DropdownMenuRadioGroupBase = DropdownMenuPrimitive.RadioGroup

const DropdownMenuSubTriggerBase = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      "flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      inset && "pl-8",
      className
    )}
    {...props}
  >
    {children}
    <ChevronRight className="ml-auto" />
  </DropdownMenuPrimitive.SubTrigger>
))
DropdownMenuSubTriggerBase.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName

const DropdownMenuSubContentBase = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
))
DropdownMenuSubContentBase.displayName =
  DropdownMenuPrimitive.SubContent.displayName

const DropdownMenuContentBase = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
))
DropdownMenuContentBase.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItemBase = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0",
      inset && "pl-8",
      className
    )}
    {...props}
  />
))
DropdownMenuItemBase.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuCheckboxItemBase = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
))
DropdownMenuCheckboxItemBase.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName

const DropdownMenuRadioItemBase = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Circle className="h-2 w-2 fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
))
DropdownMenuRadioItemBase.displayName = DropdownMenuPrimitive.RadioItem.displayName

const DropdownMenuLabelBase = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-sm font-semibold",
      inset && "pl-8",
      className
    )}
    {...props}
  />
))
DropdownMenuLabelBase.displayName = DropdownMenuPrimitive.Label.displayName

const DropdownMenuSeparatorBase = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
))
DropdownMenuSeparatorBase.displayName = DropdownMenuPrimitive.Separator.displayName

const DropdownMenuShortcutBase = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn("ml-auto text-xs tracking-widest opacity-60", className)}
      {...props}
    />
  )
}
DropdownMenuShortcutBase.displayName = "DropdownMenuShortcut"

// --- uiv2: conditional Intui swap (build-time resolved by Vite) ---
import {
  IntuiDropdownMenu,
  IntuiDropdownMenuTrigger,
  IntuiDropdownMenuContent,
  IntuiDropdownMenuItem,
  IntuiDropdownMenuLabel,
  IntuiDropdownMenuSeparator,
  IntuiDropdownMenuSub,
  IntuiDropdownMenuSubTrigger,
  IntuiDropdownMenuSubContent,
  IntuiDropdownMenuCheckboxItem,
  IntuiDropdownMenuRadioGroup,
  IntuiDropdownMenuRadioItem,
  IntuiDropdownMenuGroup,
  IntuiDropdownMenuShortcut,
} from '@/components/intueri/IntuiDropdownMenu'

const _uiv2 = import.meta.env.VITE_UIV2 === 'true'

// Lazy proxy: wraps Intui access in a render function to avoid TDZ errors from circular ESM imports.
function _lazy<T>(getIntui: () => unknown, base: T): T {
  if (!_uiv2) return base
  // Dynamic-dispatch proxy — `any` is required so the generic _lazy<T> can
  // forward refs/props to whichever Intui component getIntui() resolves at
  // render time. The outer `as unknown as T` cast re-narrows for callers.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return React.forwardRef<any, any>((props, ref) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Comp = getIntui() as any
    return <Comp ref={ref} {...props} />
  }) as unknown as T
}

const DropdownMenu = _lazy(() => IntuiDropdownMenu, DropdownMenuBase)
const DropdownMenuTrigger = _lazy(() => IntuiDropdownMenuTrigger, DropdownMenuTriggerBase)
const DropdownMenuContent = _lazy(() => IntuiDropdownMenuContent, DropdownMenuContentBase)
const DropdownMenuItem = _lazy(() => IntuiDropdownMenuItem, DropdownMenuItemBase)
const DropdownMenuCheckboxItem = _lazy(() => IntuiDropdownMenuCheckboxItem, DropdownMenuCheckboxItemBase)
const DropdownMenuRadioItem = _lazy(() => IntuiDropdownMenuRadioItem, DropdownMenuRadioItemBase)
const DropdownMenuLabel = _lazy(() => IntuiDropdownMenuLabel, DropdownMenuLabelBase)
const DropdownMenuSeparator = _lazy(() => IntuiDropdownMenuSeparator, DropdownMenuSeparatorBase)
const DropdownMenuShortcut = _lazy(() => IntuiDropdownMenuShortcut, DropdownMenuShortcutBase)
const DropdownMenuGroup = _lazy(() => IntuiDropdownMenuGroup, DropdownMenuGroupBase)
const DropdownMenuSub = _lazy(() => IntuiDropdownMenuSub, DropdownMenuSubBase)
const DropdownMenuSubTrigger = _lazy(() => IntuiDropdownMenuSubTrigger, DropdownMenuSubTriggerBase)
const DropdownMenuSubContent = _lazy(() => IntuiDropdownMenuSubContent, DropdownMenuSubContentBase)
const DropdownMenuRadioGroup = _lazy(() => IntuiDropdownMenuRadioGroup, DropdownMenuRadioGroupBase)
const DropdownMenuPortal = DropdownMenuPortalBase

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuBase,
  DropdownMenuTriggerBase,
  DropdownMenuContentBase,
  DropdownMenuItemBase,
  DropdownMenuCheckboxItemBase,
  DropdownMenuRadioItemBase,
  DropdownMenuLabelBase,
  DropdownMenuSeparatorBase,
  DropdownMenuShortcutBase,
  DropdownMenuGroupBase,
  DropdownMenuPortalBase,
  DropdownMenuSubBase,
  DropdownMenuSubContentBase,
  DropdownMenuSubTriggerBase,
  DropdownMenuRadioGroupBase,
}
